/**
 * Admin Ticket Statistics Page
 * Dashboard for admins to view ticket statistics and metrics
 */

import { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import {
  ConfirmationNumber,
  CheckCircle,
  HourglassBottom,
  Warning,
  TrendingUp,
} from '@mui/icons-material'
import { getTicketStatistics } from '@api/services/tickets.api'
import {
  TicketStatisticsDto,
  TicketStatus,
  TicketPriority,
  TicketType,
  TicketStatusLabels,
  TicketPriorityLabels,
  TicketTypeLabels,
} from '@api/types/tickets'

export default function AdminTicketStatisticsPage() {
  const [statistics, setStatistics] = useState<TicketStatisticsDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadStatistics = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getTicketStatistics()
      setStatistics(data)
    } catch (err: any) {
      console.error('Failed to load statistics:', err)
      setError(err.message || 'Failed to load statistics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatistics()
  }, [])

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error || !statistics) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="error">{error || 'Failed to load statistics'}</Alert>
      </Container>
    )
  }

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${Math.round(minutes)} minutes`
    const hours = Math.floor(minutes / 60)
    const mins = Math.round(minutes % 60)
    return `${hours}h ${mins}m`
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Ticket Statistics Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ConfirmationNumber color="primary" />
                <Typography variant="h6">{statistics.totalTickets}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HourglassBottom color="warning" />
                <Typography variant="h6">{statistics.openTickets}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Open Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircle color="success" />
                <Typography variant="h6">{statistics.closedTickets}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Closed Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Warning color="error" />
                <Typography variant="h6">{statistics.escalatedTickets}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Escalated Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Average Resolution Time */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUp color="primary" />
          <Typography variant="h6">Average Resolution Time</Typography>
        </Box>
        <Typography variant="h4" color="primary.main">
          {formatTime(statistics.averageResolutionTimeMinutes)}
        </Typography>
      </Paper>

      {/* Detailed Breakdowns */}
      <Grid container spacing={3}>
        {/* Status Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              By Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(statistics.ticketsByStatus).map(([status, count]) => {
                const statusEnum = parseInt(status) as TicketStatus
                return (
                  <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{TicketStatusLabels[statusEnum]}</Typography>
                    <Typography variant="h6" color="primary.main">
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Priority Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              By Priority
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(statistics.ticketsByPriority).map(([priority, count]) => {
                const priorityEnum = parseInt(priority) as TicketPriority
                return (
                  <Box
                    key={priority}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="body2">{TicketPriorityLabels[priorityEnum]}</Typography>
                    <Typography variant="h6" color="primary.main">
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Type Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              By Type
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(statistics.ticketsByType).map(([type, count]) => {
                const typeEnum = parseInt(type) as TicketType
                return (
                  <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {TicketTypeLabels[typeEnum]}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}
